"use client"

import { useTranslations } from "next-intl"
import { useCharacterStore } from "@/features/characters/store"
import { useAuthStore } from "@/features/auth/store"
import { isPaidTier } from "@/shared/lib/constants"
import { Label } from "@/shared/components/ui/label"
import {
  ImageCardGrid,
  ColorSwatchPicker,
  EYE_COLORS,
  HAIR_COLORS,
  HAIR_STYLE_OPTIONS,
} from "./appearance-step"
import { PremiumCustomOption } from "./premium-custom-option"
import { cn } from "@/shared/lib/utils"
import { Icon } from "@/shared/components/icons"
import { localizeCreateOptions } from "@/features/create/lib/create-option-labels"

export function HairEyesStep({ invalidFields = [] }: { invalidFields?: string[] }) {
  const t = useTranslations("create.wizard")
  const tOptions = useTranslations("create.wizard.optionLabels")
  const { draft, updateDraft } = useCharacterStore()
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = !!user
  const isPremium = isPaidTier(user?.subscription ?? "free")
  const currentGender = draft.gender || "female"
  const currentStyle = draft.style || "realistic"
  const isEyeColorInvalid = invalidFields.includes("eyeColor")
  const isHairColorInvalid = invalidFields.includes("hairColor")
  const isHairStyleInvalid = invalidFields.includes("hairStyle")
  const eyeColorOptions = localizeCreateOptions(EYE_COLORS, "eyeColor", tOptions)
  const hairColorOptions = localizeCreateOptions(HAIR_COLORS, "hairColor", tOptions)
  const hairStyleOptions = localizeCreateOptions(HAIR_STYLE_OPTIONS, "hairStyle", tOptions)

  // Custom is active when hairStyle is set and is not one of the preset values
  const isCustomHairStyle =
    draft.hairStyle !== "" && !HAIR_STYLE_OPTIONS.some((o) => o.value === draft.hairStyle)

  return (
    <div className="space-y-5">
      {/* Eye Color + Hair Color — stacked, one above the other */}
      <div className="space-y-4">
        {/* Eye Color */}
        <div
          className={cn(
            "rounded-[6px] border bg-card/50 px-2 py-3 sm:p-5 transition-all",
            isEyeColorInvalid
              ? "border-red-500/40 bg-red-500/[0.04] ring-1 ring-red-500/30"
              : "border-border/50 hover:ring-1 ring-border/20 focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary/50"
          )}
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className={cn(
              "flex size-8 items-center justify-center rounded-lg",
              isEyeColorInvalid ? "bg-red-500/15 text-red-400" : "bg-primary/10 text-primary"
            )} aria-hidden>
              <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="4" />
                <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
                <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
                <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
                <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
              </svg>
            </span>
            <Label className={cn("font-semibold text-base transition-colors", isEyeColorInvalid ? "text-red-400" : "text-foreground")}>
              {t("fieldLabels.eyeColor")}
            </Label>
          </div>
          <ColorSwatchPicker
            options={eyeColorOptions}
            selected={draft.eyeColor}
            onSelect={(v) => updateDraft({ eyeColor: v })}
          />
          {isEyeColorInvalid && (
            <div className="flex items-center justify-center gap-1.5 mt-3">
              <Icon name="attention-circle" size={14} className="size-3.5 text-red-400 shrink-0" />
              <p className="text-xs text-red-400">{t("validation.eyeColor")}</p>
            </div>
          )}
        </div>

        {/* Hair Color */}
        <div
          className={cn(
            "rounded-[6px] border bg-card/50 px-2 py-3 sm:p-5 transition-all",
            isHairColorInvalid
              ? "border-red-500/40 bg-red-500/[0.04] ring-1 ring-red-500/30"
              : "border-border/50 hover:ring-1 ring-border/20 focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary/50"
          )}
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className={cn(
              "flex size-8 items-center justify-center rounded-lg",
              isHairColorInvalid ? "bg-red-500/15 text-red-400" : "bg-primary/10 text-primary"
            )} aria-hidden>
              <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 3 Q4 8 4 12 Q4 18 9 21" />
                <path d="M15 3 Q20 8 20 12 Q20 18 15 21" />
                <path d="M6 15 Q9 17 12 17 Q15 17 18 15" />
              </svg>
            </span>
            <Label className={cn("font-semibold text-base transition-colors", isHairColorInvalid ? "text-red-400" : "text-foreground")}>
              {t("fieldLabels.hairColor")}
            </Label>
          </div>
          <ColorSwatchPicker
            options={hairColorOptions}
            selected={draft.hairColor}
            onSelect={(v) => updateDraft({ hairColor: v })}
          />
          {isHairColorInvalid && (
            <div className="flex items-center justify-center gap-1.5 mt-3">
              <Icon name="attention-circle" size={14} className="size-3.5 text-red-400 shrink-0" />
              <p className="text-xs text-red-400">{t("validation.hairColor")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Hair Style — 5 columns + Custom card */}
      <div
        className={cn(
          "space-y-4 rounded-[6px] px-1.5 py-4 transition-all",
          isHairStyleInvalid && "ring-1 ring-red-500/40 border border-red-500/30 bg-red-500/[0.03]"
        )}
      >
        <div className="flex items-center justify-center gap-2">
          <span className={cn(
            "flex size-8 items-center justify-center rounded-lg",
            isHairStyleInvalid ? "bg-red-500/15 text-red-400" : "bg-primary/10 text-primary"
          )} aria-hidden>
            <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="6" cy="6" r="3" />
              <circle cx="6" cy="18" r="3" />
              <line x1="20" y1="4" x2="8.12" y2="15.88" />
              <line x1="14.47" y1="14.48" x2="20" y2="20" />
              <line x1="8.12" y1="8.12" x2="12" y2="12" />
            </svg>
          </span>
          <Label className={cn("font-semibold text-base transition-colors", isHairStyleInvalid ? "text-red-400" : "text-foreground")}>
            {t("fieldLabels.hairStyle")}
          </Label>
        </div>

        <ImageCardGrid
          options={hairStyleOptions}
          selected={isCustomHairStyle ? "" : draft.hairStyle}
          onSelect={(v) => updateDraft({ hairStyle: v })}
          gender={currentGender}
          style={currentStyle}
          columns={5}
          compact
          customSlot={
            <PremiumCustomOption
              isAuthenticated={isAuthenticated}
              isPremium={isPremium}
              isSelected={isCustomHairStyle}
              customValue={isCustomHairStyle ? draft.hairStyle : ""}
              onCustomChange={(v) => updateDraft({ hairStyle: v })}
              placeholder={t("placeholders.customHairStyle")}
              dialogTitle={t("customDialog.hairStyleTitle")}
              variant="card"
            />
          }
        />
        {isHairStyleInvalid && (
          <div className="flex items-center justify-center gap-1.5">
            <Icon name="attention-circle" size={14} className="size-3.5 text-red-400 shrink-0" />
            <p className="text-xs text-red-400">{t("validation.hairStyle")}</p>
          </div>
        )}
      </div>
    </div>
  )
}
